import React from 'react';
import { Loader, Pagination, UnstyledButton } from '@mantine/core';
import type { AdminOrdersSortKey } from '../../../services/admin-orders/admin-orders.service';
import { formatDateTime, formatMoney } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type OrdersMonitoringTableProps = Pick<
  UseMonitoringPage,
  | 'ordersRows'
  | 'isOrdersLoading'
  | 'hasLoadedOrders'
  | 'ordersSortKey'
  | 'ordersSortDirection'
  | 'toggleOrdersSort'
  | 'ordersTotalPages'
  | 'ordersPage'
  | 'setOrdersPage'
  | 'openOrderDetail'
>;

const SORT_LABELS: Record<AdminOrdersSortKey, string> = {
  companyName: 'חברה',
  sessionName: 'משמרת',
  orderCode: 'מספר הזמנה',
  tableLabels: 'שולחן/שולחנות',
  totalAmount: 'סה"כ הזמנה',
  openedAt: 'מתי נפתחה',
  closedAt: 'מתי נסגרה',
  status: 'סטטוס',
};

export const OrdersMonitoringTable: React.FC<OrdersMonitoringTableProps> = ({
  ordersRows,
  isOrdersLoading,
  hasLoadedOrders,
  ordersSortKey,
  ordersSortDirection,
  toggleOrdersSort,
  ordersTotalPages,
  ordersPage,
  setOrdersPage,
  openOrderDetail,
}) => {
  const renderSortableHeader = (key: AdminOrdersSortKey) => {
    const isActive = ordersSortKey === key;
    const indicator = !isActive ? '↕' : ordersSortDirection === 'asc' ? '↑' : '↓';

    return (
      <UnstyledButton
        className={isActive ? `${classes.sortButton} ${classes.sortButtonActive}` : classes.sortButton}
        onClick={() => toggleOrdersSort(key)}
        data-testid={`monitoring-orders-sort-${key}`}
      >
        <span>{SORT_LABELS[key]}</span>
        <span className={classes.sortIndicator} aria-hidden="true">{indicator}</span>
      </UnstyledButton>
    );
  };

  if (isOrdersLoading && !hasLoadedOrders) {
    return <div className={classes.loadingWrap}><Loader /></div>;
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>{renderSortableHeader('sessionName')}</th>
            <th>{renderSortableHeader('tableLabels')}</th>
            <th>{renderSortableHeader('orderCode')}</th>
            <th>{renderSortableHeader('totalAmount')}</th>
            <th>{'מי פתח'}</th>
            <th>{renderSortableHeader('openedAt')}</th>
            <th>{renderSortableHeader('closedAt')}</th>
          </tr>
        </thead>
        <tbody>
          {ordersRows.map((row, index) => (
            <tr key={row.id} className={classes.clickableRow} onClick={() => openOrderDetail(index)} data-testid={`monitoring-orders-row-${row.id}`}>
              <td data-label={'משמרת'}>{row.sessionName}</td>
              <td data-label={'שולחנות'}>{row.tableLabels.length ? row.tableLabels.join(', ') : '-'}</td>
              <td data-label={'מספר הזמנה'}>{row.orderCode}</td>
              <td data-label={'סה"כ הזמנה'}>{`${formatMoney(row.totalAmount)} ₪`}</td>
              <td data-label={'מי פתח'}>{row.openedByName ?? '-'}</td>
              <td data-label={'מתי נפתחה'}>{formatDateTime(row.openedAt)}</td>
              <td data-label={'מתי נסגרה'}>{formatDateTime(row.closedAt)}</td>
            </tr>
          ))}
          {ordersRows.length === 0 && (
            <tr>
              <td colSpan={7} className={classes.empty}>{'אין הזמנות התואמות לסינון הנוכחי.'}</td>
            </tr>
          )}
        </tbody>
      </table>
      {ordersTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={ordersTotalPages} value={ordersPage} onChange={setOrdersPage} size="sm" />
        </div>
      )}
    </div>
  );
};
